import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getBilateralAgreementsRequest,
  getBilateralAgreementsHistoryRequest,
} from "../../../redux/actions/bilateralAgreements";
import EmptyState from "../../../components/EmptyState/EmptyState";
import Loading from "../../../components/Loading";
import DataTable from "../../../components/DataTable";
import moment from "moment";
import Button from "../../../components/Button";
import Pill from "../../../components/Pill";
import ModalConfirmation from "../../../components/Modal/ModalConfirmation";
import { Input, Select } from "../../../components/Inputs";
import Checkbox from "../../../components/Checkbox/Checkbox";
import DatePicker from "react-datepicker";
import BilateralDynamicModal from "../../../components/Modal/BilateralDynamicModal";
import { API_EXPORT, API_PUT } from "../../../helpers/api";
import { setNotification } from "../../../helpers/notification";
import fileDownload from "js-file-download";
import CancelBilateralModal from "../../../components/Modal/CancelBilateralModal";
import DataTableServerSideRendering from "../../../components/DataTable/DataTableServerSideRendering";
import { convertAmountToFrontend } from "../../../helpers/convert";

const BilateralAgreementOperations = ({ t }) => {
  const dispatch = useDispatch();
  const organizationId = useSelector(
    (state) => state.authorization.user.organization.organizationId
  );
  const authUser = useSelector((state) => state.authorization.user);

  const bilateralAgreementsList = useSelector(
    (state) => state.bilateralAgreements.list
  );
  const loading = useSelector((state) => state.bilateralAgreements.loading);
  const {
    historyList: bilateralAgreementsHistroyList,
    total,
    buyerTotal,
  } = useSelector((state) => state.bilateralAgreements);

  const historyLoading = useSelector(
    (state) => state.bilateralAgreements.historyLoading
  );

  const [
    showBilateralAgreementAprroveModal,
    setShowBilateralAgreementAprroveModal,
  ] = useState(false);
  const [selectedBilateralAgreement, setSelectedBilateralAgreement] = useState(
    {}
  );
  const [sellerOption, setSellerOption] = useState(false);
  const [buyerOption, setBuyerOption] = useState(false);
  const [filterStatus, setFilterStatus] = useState("SUCCESSFUL,CANCELED");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortedColumn, setSortedColumn] = useState("productionPeriod");
  const [sortDirection, setSortDirection] = useState("desc");
  const [activePage, setActivePage] = useState(0);
  const [bilateralAgreementDetailsModal, setBilateralAgreementDetailsModal] =
    useState(false);
  const [cancelMode, setCancelMode] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [downloadFilter, setDownloadFilter] = useState({
    status: "SUCCESSFUL,CANCELED",
  });

  const selectStatus = [
    {
      name: t("pages.default.certificates.filters.allStatus"),
      value: "SUCCESSFUL,CANCELED",
    },
    { name: t("ui.default.codes.CANCEL"), value: "CANCELED" },
    { name: t("ui.default.codes.SUCCESSFUL"), value: "SUCCESSFUL" },
  ];

  const bilateralAgreementsColumns = [
    {
      name: t("pages.default.bilateralAgreements.table.seller"),
      selector: "fromOrganizationName",
      sortable: true,
      showTitle: true,
      cell: (row) => (
        <div
          className={`${
            row.fromOrganizationId === organizationId
              ? "km__p--bilateral-agreements-active-organization"
              : ""
          }`}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.fromOrganizationName}
        </div>
      ),
    },
    {
      name: t("pages.default.bilateralAgreements.table.buyer"),
      selector: "toOrganizationName",
      sortable: true,
      showTitle: true,
      cell: (row) => (
        <div
          className={`${
            row.toOrganizationId === organizationId
              ? "km__p--bilateral-agreements-active-organization"
              : ""
          }`}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.toOrganizationName}
        </div>
      ),
    },
    {
      name: t(
        "pages.default.bilateralAgreements.table.bilateralAgreementPeriod"
      ),
      selector: "period",
      sortable: true,
      cell: (row) =>
        row.createdDate
          ? `${t(
              `ui.default.months.${moment(row.createdDate).format("M")}`
            )} ${moment(row.createdDate).format("YYYY")}`
          : "-",
    },
    {
      name: t("pages.default.bilateralAgreements.table.transaction_date"),
      selector: "createdDate",
      sortable: true,
      cell: (row) =>
        row.createdDate
          ? moment(row.createdDate).format("DD.MM.YYYY HH:mm:ss")
          : "-",
    },
    {
      name: t("pages.default.bilateralAgreements.table.documentCount"),
      selector: "amount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.amount);
      },
    },
    {
      name: t("pages.default.bilateralAgreements.table.status"),
      selector: "status",
      sortable: false,
      cell: (row) => {
        switch (row.status) {
          case "INITIAL":
            if (row.fromOrganizationId === organizationId) {
              return (
                <Pill
                  type="attention"
                  label={t(
                    "pages.default.bilateralAgreements.status.pendingApproval"
                  )}
                />
              );
            }
            return (
              <Pill
                type="important"
                label={t(
                  "pages.default.bilateralAgreements.status.waitingForYourApproval"
                )}
              />
            );
          case "AGREEMENT":
            return (
              <Pill
                type="success"
                label={t(
                  "pages.default.bilateralAgreements.status.bilateralConfirmationReceived"
                )}
              />
            );
          default:
            return;
        }
      },
    },
    {
      name: t("pages.default.bilateralAgreements.table.review"),
      selector: "review",
      sortable: false,
      cell: (row, index) => (
        <div style={{ display: "flex" }}>
          <Button
            title={t("ui.default.review")}
            suffixIcon="search"
            type="secondary"
            onClick={() => handleSetBilateralAgreementDetailsModal(row, false)}
            id={`ba-review-${index}`}
          />
        </div>
      ),
    },
    {
      name: t("pages.default.bilateralAgreements.table.process"),
      selector: "process",
      sortable: false,
      width: "140px",
      cell: (row, index) => (
        <div style={{ display: "flex" }}>
          <Button
            title={t("ui.default.codes.CANCEL")}
            suffixIcon="x"
            type="destructive"
            disabled={
              authUser.organization.yekgOrganizationStatus === "PASSIVE"
            }
            onClick={() => handleSetCancelBilateralModal(row)}
            id={`ba-cancel-${index}`}
          />
          {(row.status === "AGREEMENT" ||
            row.fromOrganizationId !== organizationId) && (
            <Button
              title={t("ui.default.accept")}
              suffixIcon="check"
              type="primary"
              disabled={
                row.status === "AGREEMENT" ||
                authUser.organization.yekgOrganizationStatus === "PASSIVE"
              }
              onClick={() => handleSetShowBialteralAgreementAprroveModal(row)}
              id={`ba-accept-${index}`}
            />
          )}
        </div>
      ),
    },
  ];

  const bilateralAgreementsHistoryColumns = [
    {
      name: t("pages.default.bilateralAgreements.table.seller"),
      selector: "fromOrganizationName",
      sortable: true,
      showTitle: true,
      cell: (row) => (
        <div
          className={`${
            row.fromOrganizationId === organizationId
              ? "km__p--bilateral-agreements-active-organization"
              : ""
          }`}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.fromOrganizationName}
        </div>
      ),
    },
    {
      name: t("pages.default.bilateralAgreements.table.buyer"),
      selector: "toOrganizationName",
      sortable: true,
      showTitle: true,
      cell: (row) => (
        <div
          className={`${
            row.toOrganizationId === organizationId
              ? "km__p--bilateral-agreements-active-organization"
              : ""
          }`}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.toOrganizationName}
        </div>
      ),
    },
    {
      name: t(
        "pages.default.bilateralAgreements.table.bilateralAgreementPeriod"
      ),
      selector: "period",
      sortable: true,
      cell: (row) =>
        row.createdDate
          ? `${t(
              `ui.default.months.${moment(row.createdDate).format("M")}`
            )} ${moment(row.createdDate).format("YYYY")}`
          : "-",
    },
    {
      name: t("pages.default.bilateralAgreements.table.transaction_date"),
      selector: "createdDate",
      sortable: true,
      cell: (row) =>
        row.createdDate
          ? moment(row.createdDate).format("DD.MM.YYYY HH:mm:ss")
          : "",
    },
    {
      name: t("pages.default.bilateralAgreements.table.documentCount"),
      selector: "amount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.amount);
      },
    },
    {
      name: t("pages.default.bilateralAgreements.table.status"),
      selector: "status",
      sortable: false,
      cell: (row) => {
        switch (row.status) {
          case "SUCCESSFUL":
            return (
              <Pill type="success" label={t("ui.default.codes.SUCCESSFUL")} />
            );
          case "CANCELED":
            return (
              <Pill type="important" label={t("ui.default.codes.CANCEL")} />
            );
          default:
            return;
        }
      },
    },
    {
      name: t("pages.default.bilateralAgreements.table.review"),
      selector: "review",
      sortable: false,
      cell: (row, index) => (
        <div style={{ display: "flex" }}>
          <Button
            title={t("ui.default.review")}
            suffixIcon="search"
            type="secondary"
            onClick={() => handleSetBilateralAgreementDetailsModal(row, false)}
            id={`ba-history-review-${index}`}
          />
        </div>
      ),
    },
  ];

  useEffect(() => {
    dispatch(getBilateralAgreementsRequest(organizationId));
    dispatch(
      getBilateralAgreementsHistoryRequest({
        sellerApiFilters: {
          toOrganizationId: organizationId,
          status: "SUCCESSFUL,CANCELED",
        },
        buyerApiFilters: {
          fromOrganizationId: organizationId,
          status: "SUCCESSFUL,CANCELED",
        },
      })
    );
  }, [dispatch, organizationId]);

  const handleSetShowBialteralAgreementAprroveModal = (row) => {
    setShowBilateralAgreementAprroveModal(true);
    setSelectedBilateralAgreement({ ...row });
  };

  const updateBilateralAgreementStatus = async (reqParams) => {
    try {
      setLoadingRequest(true);
      const bilateralAgreement = await API_PUT(
        `bilateralAgreement/${selectedBilateralAgreement.id}`,
        null,
        { ...reqParams }
      );
      showBilateralAgreementAprroveModal &&
        setShowBilateralAgreementAprroveModal(false);
      bilateralAgreementDetailsModal &&
        setBilateralAgreementDetailsModal(false);
      showCancelModal && setShowCancelModal(false);
      setLoadingRequest(false);
      setSelectedBilateralAgreement({});

      bilateralAgreement?.data?.status === "AGREEMENT"
        ? setNotification(
            "success",
            t("pages.default.bilateralAgreements.approvalSuccess")
          )
        : setNotification(
            "success",
            t("pages.default.bilateralAgreements.approvalCancelled")
          );

      dispatch(getBilateralAgreementsRequest(organizationId));
    } catch (err) {
      setLoadingRequest(false);
      setNotification("error", err.response.data.message);
    }
  };

  const handleSetBilateralAgreementDetailsModal = (row, cancelModeValue) => {
    setBilateralAgreementDetailsModal(true);
    setSelectedBilateralAgreement({ ...row });
    cancelModeValue !== cancelMode && setCancelMode(cancelModeValue);
  };

  const handleSetCancelBilateralModal = (row) => {
    setShowCancelModal(true);
    setSelectedBilateralAgreement({ ...row });
  };

  const hanldeCheckBoxChange = (e) => {
    const { name } = e.target;
    switch (name) {
      case "sellerOption":
        setSellerOption((prevState) => !prevState);
        break;
      case "buyerOption":
        setBuyerOption((prevState) => !prevState);
        break;
      default:
        break;
    }
  };

  const filterBilateralAgreementsHistory = () => {
    let sellerApiFilters = {
      toOrganizationId: organizationId,
      status: filterStatus,
      startDate: startDate ? moment(startDate).format("DD-MM-YYYY") : "",
      endDate: endDate ? moment(endDate).format("DD-MM-YYYY") : "",
    };
    let buyerApiFilters = {
      fromOrganizationId: organizationId,
      status: filterStatus,
      startDate: startDate ? moment(startDate).format("DD-MM-YYYY") : "",
      endDate: endDate ? moment(endDate).format("DD-MM-YYYY") : "",
    };
    setDownloadFilter({
      fromOrganizationId: sellerOption && !buyerOption ? organizationId : null,
      toOrganizationId: buyerOption && !sellerOption ? organizationId : null,
      startDate: startDate ? moment(startDate).format("DD-MM-YYYY") : "",
      endDate: endDate ? moment(endDate).format("DD-MM-YYYY") : "",
      status: filterStatus,
    });
    switch (true) {
      case buyerOption && !sellerOption:
        return dispatch(
          getBilateralAgreementsHistoryRequest({ sellerApiFilters })
        );
      case sellerOption && !buyerOption:
        return dispatch(
          getBilateralAgreementsHistoryRequest({ buyerApiFilters })
        );
      default:
        return dispatch(
          getBilateralAgreementsHistoryRequest({
            sellerApiFilters,
            buyerApiFilters,
          })
        );
    }
  };

  const downloadBilateralAgreements = async () => {
    setExportLoading(true);
    try {
      const res = await API_EXPORT("bilateralAgreement/export/excel", {
        ...downloadFilter,
        lang: window.localStorage.i18nextLng,
      });
      const [, filename] =
        res?.headers["content-disposition"].split("filename=");
      fileDownload(res.data, filename);
      setExportLoading(false);
      setNotification("success", t("ui.default.downloaded"));
    } catch (err) {
      err.response && setNotification("error", err.response.data.message);
      setExportLoading(false);
    }
  };
  const handleClick = (activePage, selector, direction) => {
    console.log("page", activePage, selector, direction);
    setActivePage(activePage);
    dispatch(
      getBilateralAgreementsHistoryRequest({
        sellerApiFilters: {
          toOrganizationId: organizationId,
          status: "SUCCESSFUL,CANCELED",
          page: activePage,
          selector: selector,
          direction: direction,
        },
        buyerApiFilters: {
          fromOrganizationId: organizationId,
          status: "SUCCESSFUL,CANCELED",
          page: activePage,
          selector: selector,
          direction: direction,
        },
      })
    );
  };
  return (
    <>
      <h5 className="km__u--text-has-emphasis">
        {t(
          "pages.default.bilateralAgreements.bilateralAgreementOperationsTitle"
        )}
      </h5>
      <div className="km__p--card">
        <div className="km__p--bilateral-agreements-filter-container">
          <div className="km__u--text_title">
            {t("pages.default.bilateralAgreements.activeOperationsTitle")}
          </div>
        </div>

        {!loading && (
          <DataTable
            columns={bilateralAgreementsColumns}
            data={bilateralAgreementsList}
            rowKey={"id"}
            defaultSortedField={"createdDate"}
            pagination
            paginationPerPage={10}
          />
        )}
        {bilateralAgreementsList &&
          bilateralAgreementsList.length === 0 &&
          !loading && (
            <EmptyState
              icon="file-text"
              text={t("pages.default.bilateralAgreements.activeNotFound")}
            />
          )}
        {loading && (
          <Loading
            text={t("pages.default.bilateralAgreements.activeLoading")}
          />
        )}
      </div>

      <div className="km__p--card">
        <div className="km__p--bilateral-agreements-header">
          <div className="km__u--text_title">
            {t("pages.default.bilateralAgreements.operationHistoryTitle")}
          </div>
        </div>
        <div className="km__p--bilateral-agreements-filters km__p--bilateral-agreements-history-filter-container">
          <div>
            <Checkbox
              label={t(
                "pages.default.bilateralAgreements.input.sellOperations"
              )}
              name="sellerOption"
              checked={sellerOption}
              onChange={hanldeCheckBoxChange}
              id="ba-checkbox-sellerOption"
            />
          </div>
          <div>
            <Checkbox
              label={t("pages.default.bilateralAgreements.input.buyOperations")}
              name="buyerOption"
              checked={buyerOption}
              onChange={hanldeCheckBoxChange}
              id="ba-checkbox-buyerOption"
            />
          </div>
          <Input
            type="date"
            placeholder={t("pages.default.bilateralAgreements.input.startDate")}
            max={
              new Date(moment(endDate).subtract(1, "days").format("MM-DD-YYYY"))
            }
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            id="ba-startDate"
          />
          <Input
            type="date"
            placeholder={t("pages.default.bilateralAgreements.input.endDate")}
            min={
              new Date(moment(startDate).add(1, "days").format("MM-DD-YYYY"))
            }
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            id="ba-endDate"
          />
          <Select
            name="bilateralHistoryStatus"
            defaultValue={filterStatus}
            options={selectStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            id="ba-bilateralHistoryStatus"
          />
          <Button
            type="primary"
            label={t("ui.default.filter")}
            onClick={filterBilateralAgreementsHistory}
            id="ba-filter"
          />
          <Button
            type="secondary"
            label={t("ui.default.download")}
            suffixIcon="download"
            onClick={downloadBilateralAgreements}
            disabled={exportLoading}
            id="ba-download"
          />
        </div>
        {!historyLoading && (
          // <DataTable
          //   columns={bilateralAgreementsHistoryColumns}
          //   data={bilateralAgreementsHistroyList}
          //   rowKey={'id'}
          //   pagination
          //   paginationPerPage={10}
          //   defaultSortedField='createdDate'
          // />
          <DataTableServerSideRendering
            columns={bilateralAgreementsHistoryColumns}
            data={bilateralAgreementsHistroyList}
            rowKey="id"
            sortedColumn={sortedColumn}
            setSortedColumn={setSortedColumn}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            pagination
            activePage={activePage}
            totalPageCount={
              total ? total?.totalPageCount : buyerTotal?.totalPageCount
            }
            handleFetchData={handleClick}
          />
        )}
        {bilateralAgreementsHistroyList &&
          bilateralAgreementsHistroyList.length === 0 &&
          !historyLoading && (
            <EmptyState
              icon="file-text"
              text={t("pages.default.bilateralAgreements.historyNotFound")}
            />
          )}
        {historyLoading && (
          <Loading
            text={t("pages.default.bilateralAgreements.historyLoading")}
          />
        )}
      </div>
      <CancelBilateralModal
        t={t}
        isOpen={showCancelModal}
        toggle={() => setShowCancelModal(false)}
        selectedAgreement={selectedBilateralAgreement}
        cancelOnclick={updateBilateralAgreementStatus}
        loading={loadingRequest}
      />
      <ModalConfirmation
        isOpen={showBilateralAgreementAprroveModal}
        toggle={() =>
          setShowBilateralAgreementAprroveModal(
            !showBilateralAgreementAprroveModal
          )
        }
        header={t(
          "pages.default.bilateralAgreements.bilateralApproveConfirmation.header"
        )}
        body={t(
          "pages.default.bilateralAgreements.bilateralApproveConfirmation.body"
        )}
        approve={() => updateBilateralAgreementStatus({ status: "AGREEMENT" })}
        loading={loadingRequest}
      />
      <BilateralDynamicModal
        t={t}
        isOpen={bilateralAgreementDetailsModal}
        toggle={() =>
          setBilateralAgreementDetailsModal(!bilateralAgreementDetailsModal)
        }
        selectedAgreement={selectedBilateralAgreement}
        cancelMode={cancelMode}
        organizationId={organizationId}
      />
    </>
  );
};

export default BilateralAgreementOperations;
